import { useCallback, useEffect, useState } from "react";
import { api } from "@/lib/api";
import { logger } from "@/lib/logger";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { ShieldAlert, UserCheck, Check } from "lucide-react";

// Accounts locked after repeated failed logins — admin can unlock or dismiss.
export default function SecurityAlerts({ lockoutTick, onChanged }) {
  const [alerts, setAlerts] = useState([]);
  const load = useCallback(async () => {
    try { const res = await api.get(`/users/security-alerts`); setAlerts(res?.alerts || []); }
    catch (e) { logger.error("Failed to load security alerts:", e); }
  }, []);
  useEffect(() => { load(); }, [load, lockoutTick]);

  const drop = (id) => { setAlerts((a) => a.filter((x) => x.id !== id)); onChanged?.(); };
  const ack = async (id) => {
    try { await api.post(`/users/security-alerts/${id}/ack`); drop(id); }
    catch (e) { logger.error("Dismiss alert failed:", e); toast.error("Could not dismiss alert"); }
  };
  const unlock = async (id, username) => {
    try { await api.post(`/users/security-alerts/${id}/unlock`); drop(id); toast.success(`@${username} unlocked — they can sign in now`); }
    catch (e) { logger.error("Unlock failed:", e); toast.error("Could not unlock account"); }
  };

  if (!alerts.length) return null;
  return (
    <div className="xl:col-span-3 rounded-xl border border-red-300 bg-red-50 shadow-sm p-4" data-testid="lockout-alerts">
      <div className="flex items-center gap-2 mb-3">
        <ShieldAlert className="h-5 w-5 text-red-600" />
        <h3 className="font-display text-base font-semibold text-red-700">Security Alerts</h3>
        <span className="text-xs text-red-500">— {alerts.length} account(s) locked after repeated failed logins</span>
      </div>
      <ul className="space-y-2">
        {alerts.map((a) => (
          <li key={a.id} className="flex items-center justify-between bg-white rounded-lg border border-red-200 px-3 py-2" data-testid="lockout-alert-row">
            <div className="text-sm text-slate-700">
              <span className="font-semibold">@{a.username}</span>
              {a.name ? <span className="text-slate-400"> ({a.name})</span> : null}
              <span className="text-slate-500"> — {a.attempts} failed attempts · locked for {a.lock_minutes} min</span>
              <span className="block text-[11px] text-slate-400">{a.at ? new Date(a.at).toLocaleString("en-IN") : ""}</span>
            </div>
            <div className="flex items-center gap-2">
              <Button size="sm" className="h-7 text-[11px] bg-emerald-600 hover:bg-emerald-700" onClick={() => unlock(a.id, a.username)} data-testid={`unlock-lockout-${a.username}`}>
                <UserCheck className="h-3 w-3 mr-1" /> Unlock now
              </Button>
              <Button size="sm" variant="outline" className="h-7 text-[11px] border-red-300 text-red-600 hover:bg-red-50" onClick={() => ack(a.id)} data-testid={`ack-lockout-${a.username}`}>
                <Check className="h-3 w-3 mr-1" /> Dismiss
              </Button>
            </div>
          </li>
        ))}
      </ul>
    </div>
  );
}
